/**
 * @typedef {object} DocumentOwnedState
 * @property {object} design - plain-JS site design data (mirrors `SiteDesignStore.siteDesign.data.toJS()`)
 * @property {object} page - plain-JS current page data (mirrors `SiteDesignStore.sitePage.data.toJS()`)
 * @property {object} selected - plain-JS current selection (mirrors `SiteDesignStore.selected.toJS()`)
 * @property {string|null} blockContainerType - `SITE_ITEM_SITE`/`SITE_ITEM_PAGE`, mirrors `SiteDesignStore.blockContainerType`
 */

/**
 * @typedef {object} DocumentStatePlain
 * @property {object} selected
 * @property {object} designData
 * @property {object} pageData
 * @property {string|null} blockContainerType
 */

/**
 * The owned plain-JS state shape when `feature.documentCore` is on. Mirrors
 * the legacy `SiteDesignStore` instance fields (`siteDesign`/`sitePage`/
 * `selected`/`blockContainerType`) but as plain JS, not Immutable.
 *
 * NOTE: this shape must NEVER gain a top-level `nextState` key --
 * `normalizeHandlerResult` (`DocumentStore.js`) discriminates a handler's
 * bare-next-state return from its `{ nextState, sideEffects }` return
 * solely via `"nextState" in result`, so a bare state containing its own
 * `nextState` field would be misread as the tuple shape.
 * @returns {DocumentOwnedState}
 */
export const createInitialState = () => ({
  design: {},
  page: {},
  selected: {},
  blockContainerType: null,
});

/**
 * Reads the owned state in the same shape as the legacy
 * `SiteDesignStore.getCurrentState()` (`src/stores/SiteDesignStore.js` @3003),
 * plain-JS instead of Immutable. Used by `DocumentStore#getCurrentStatePlain`
 * and, downstream, by undo/autosave once they source from the owner (Task 11).
 * @param {DocumentOwnedState} state
 * @returns {DocumentStatePlain}
 */
export const getCurrentStatePlain = (state) => ({
  selected: state.selected,
  designData: state.design,
  pageData: state.page,
  blockContainerType: state.blockContainerType,
});

/**
 * Writes a full `DocumentStatePlain` snapshot back into the owned state
 * shape, mirroring the legacy `SiteDesignStore.applyState(data)` (@3012):
 * an unconditional overwrite of all four fields, not a partial merge.
 * @param {DocumentOwnedState} _state - unused (present for interface parity with `applyState`; overwrite is total)
 * @param {DocumentStatePlain} next
 * @returns {DocumentOwnedState}
 */
export const setStatePlain = (_state, next) => ({
  design: next.designData,
  page: next.pageData,
  selected: next.selected,
  blockContainerType: next.blockContainerType,
});
